import { foregroundCoordinator } from "./foregroundCoordinator";

export type ExcelDateSystem = "1900" | "1904";

export interface ParsedIsoDateTime {
  canonicalText: string;
  epochDays: number;
}

export interface ExcelDateSystemProbeOptions {
  /** Workbook captured by the run; omitted for the legacy active-workbook path. */
  pinnedWorkbookName?: string;
  /** A worksheet in the pinned workbook, required to activate a pin safely. */
  sheetName?: string;
  signal?: AbortSignal;
}

const MILLISECONDS_PER_DAY = 86_400_000;
const EXCEL_1900_UNIX_EPOCH_SERIAL = 25_569;
const EXCEL_1904_UNIX_EPOCH_SERIAL = 24_107;

function validUtcDate(
  year: number,
  month: number,
  day: number,
): number | null {
  const value = new Date(0);
  value.setUTCHours(0, 0, 0, 0);
  value.setUTCFullYear(year, month - 1, day);
  return value.getUTCFullYear() === year &&
    value.getUTCMonth() === month - 1 &&
    value.getUTCDate() === day
    ? value.getTime()
    : null;
}

/**
 * Parses the strict ISO date/datetime values returned by tabular execution.
 * Timezone-less datetimes preserve their wall-clock components; explicit
 * offsets are normalized to the represented instant.
 */
export function parseStrictIsoDateTime(
  rawValue: string,
): ParsedIsoDateTime | null {
  const value = rawValue.trim();
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:\d{2})?)?$/,
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dateMilliseconds = validUtcDate(year, month, day);
  if (dateMilliseconds === null) return null;

  const hasTime = match[4] !== undefined;
  const hour = hasTime ? Number(match[4]) : 0;
  const minute = hasTime ? Number(match[5]) : 0;
  const second = match[6] === undefined ? 0 : Number(match[6]);
  if (hour > 23 || minute > 59 || second > 59) return null;

  const fractionText = match[7] ?? "";
  const fractionalSeconds = fractionText
    ? Number(`0.${fractionText}`)
    : 0;
  const zone = match[8] ?? "";
  let offsetMinutes = 0;
  if (zone && zone !== "Z") {
    const sign = zone[0] === "-" ? -1 : 1;
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    if (
      offsetHour > 14 ||
      offsetMinute > 59 ||
      (offsetHour === 14 && offsetMinute !== 0)
    ) {
      return null;
    }
    offsetMinutes = sign * (offsetHour * 60 + offsetMinute);
  }

  const wallClockDays =
    (hour * 3_600 + minute * 60 + second + fractionalSeconds) / 86_400;
  const epochDays =
    dateMilliseconds / MILLISECONDS_PER_DAY +
    wallClockDays -
    offsetMinutes / 1_440;
  const canonicalFraction = fractionText.replace(/0+$/, "");
  const canonicalText = hasTime
    ? `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${String(
        second,
      ).padStart(2, "0")}${
        canonicalFraction ? `.${canonicalFraction}` : ""
      }${zone}`
    : `${match[1]}-${match[2]}-${match[3]}`;
  return { canonicalText, epochDays };
}

export function excelSerialFromIso(
  rawValue: string,
  dateSystem: ExcelDateSystem,
): { serial: number; canonicalText: string } | null {
  const parsed = parseStrictIsoDateTime(rawValue);
  if (!parsed) return null;
  let serial =
    parsed.epochDays +
    (dateSystem === "1900"
      ? EXCEL_1900_UNIX_EPOCH_SERIAL
      : EXCEL_1904_UNIX_EPOCH_SERIAL);
  // Excel's 1900 system contains the historical fictitious 1900-02-29 at
  // serial 60. Correct real dates before that boundary by one day.
  if (dateSystem === "1900" && serial < 61) serial -= 1;
  return {
    serial,
    canonicalText: parsed.canonicalText,
  };
}

export function recognizeExcelDateSystem(
  probeValue: unknown,
): ExcelDateSystem | null {
  return probeValue === 36_526
    ? "1900"
    : probeValue === 35_064
      ? "1904"
      : null;
}

/**
 * Uses the workbook's production Office.js function path without creating a
 * worksheet or writing a scratch cell. A pinned probe temporarily activates
 * the target through the foreground coordinator and fails closed if activation
 * or the in-batch workbook identity check cannot prove the correct workbook.
 */
export async function probeExcelDateSystem(
  options: ExcelDateSystemProbeOptions = {},
): Promise<ExcelDateSystem | null> {
  if (typeof Excel === "undefined" || typeof Excel.run !== "function") {
    return null;
  }
  const { pinnedWorkbookName, sheetName, signal } = options;
  const normalizedPinnedName = pinnedWorkbookName?.trim().toLowerCase();
  try {
    return await foregroundCoordinator.runInForeground(
      { workbookName: pinnedWorkbookName, sheet: sheetName },
      async ({ activationSucceeded }) => {
        if (!activationSucceeded) return null;
        return Excel.run(async (context) => {
          const workbook = context.workbook;
          const result = workbook.functions.date(2000, 1, 1);
          workbook.load("name");
          result.load("value");
          await context.sync();
          if (
            normalizedPinnedName &&
            workbook.name.trim().toLowerCase() !== normalizedPinnedName
          ) {
            return null;
          }
          return recognizeExcelDateSystem(result.value);
        });
      },
      { signal, contextLabel: "date-system probe" },
    );
  } catch {
    return null;
  }
}
